import { useState } from 'react';
import useErrorState from '../../../../context/error/useErrorState';
import useModalState from '../../../../context/modal/useModalState';
import EditPositionAdjust, { EditPositionAdjustProps } from './Adjust';
import EditPositionDeposit, { EditPositionDepositProps } from './Deposit';
import EditPositionSize, { EditPositionSizeProps } from './Size';
import EditPositionWithdraw, { EditPositionWithdrawProps } from './Withdraw';
import Button from '../../../../components/Button';
import clsx from 'clsx';
import EditPositionWrapper from './EditPositionWrapper';
import { UserPositionRaw } from '../../../../hooks/web3/trade/useUserPositions';
import { UserOrder } from '../../../../hooks/web3/trade/useUserOpenOrders';

export enum EditPositionType {
    ADJUST = 'ADJUST',
    DEPOSIT = 'DEPOSIT',
    WITHDRAW = 'WITHDRAW',
    SIZE = 'SIZE',
}

type RenderType<T> = {
    component: React.FC<T>;
    props: Omit<T, 'children'>;
};

type RenderEditPositionType = {
    [EditPositionType.ADJUST]: RenderType<EditPositionAdjustProps>;
    [EditPositionType.DEPOSIT]: RenderType<EditPositionDepositProps>;
    [EditPositionType.WITHDRAW]: RenderType<EditPositionWithdrawProps>;
    [EditPositionType.SIZE]: RenderType<EditPositionSizeProps>;
};

type EditPositionProps = {
    onClose: () => void;
};

export type Mark = {
    label: string;
    index: number;
};

const MARKS: Mark[] = [
    {
        label: '1.1',
        index: 0,
    },
    {
        label: '15',
        index: 15,
    },
    {
        label: '30',
        index: 30,
    },
    {
        label: '45',
        index: 45,
    },
    {
        label: '60',
        index: 60,
    },
    {
        label: '75',
        index: 75,
    },
    {
        label: '90',
        index: 90,
    },
    {
        label: '100',
        index: 100,
    },
];

const EditPosition: React.FC<EditPositionProps> = ({ onClose }) => {
    const { setError } = useErrorState();
    const { modalData, closeModal } = useModalState();

    const [positionType, setPositionType] = useState<EditPositionType>(
        modalData.data.defaultEditTab || EditPositionType.ADJUST
    );
    const [orderCancelled, setOrderCancelled] = useState(false);

    const render: RenderEditPositionType = {
        ADJUST: {
            component: EditPositionAdjust,
            props: { marks: MARKS, orderCancelled, setOrderCancelled },
        },
        DEPOSIT: {
            component: EditPositionDeposit,
            props: { orderCancelled, setOrderCancelled },
        },
        WITHDRAW: {
            component: EditPositionWithdraw,
            props: { orderCancelled, setOrderCancelled },
        },
        SIZE: {
            component: EditPositionSize,
            props: { marks: MARKS, orderCancelled, setOrderCancelled },
        },
    };

    const userPosition: UserPositionRaw | UserOrder | undefined = modalData.data.position;
    const userOrder =
        userPosition && 'orderData' in userPosition ? userPosition : undefined;

    const handleConfirm = async (
        confirm: () => Promise<void>,
        validate: () => boolean
    ) => {
        try {
            if (!validate()) return;

            await confirm();

            // returning here because we don't want modal to close
            if (userOrder && !orderCancelled) return;

            onClose();
        } catch (error) {
            setError(error);
        } finally {
            closeModal('waitingModal');
        }
    };

    return (
        <>
            <div className='position-type-buttons-container'>
                {Object.values(EditPositionType).map((type) => {
                    const active = positionType === type;

                    return (
                        <Button
                            key={type}
                            className={clsx({ active })}
                            onClick={() => setPositionType(type)}
                            tabIndex={active ? -1 : 0}
                        >
                            {type.toLowerCase()}
                        </Button>
                    );
                })}
            </div>
            <EditPositionWrapper
                component={render[positionType].component as React.FC}
                props={render[positionType].props}
            >
                {({ confirm, validate, approve, isApproved }) =>
                    isApproved ? (
                        userOrder && positionType === modalData.data.defaultEditTab ? (
                            <div className='action-buttons-container'>
                                <Button
                                    onClick={() => handleConfirm(confirm, validate)}
                                    disabled={orderCancelled}
                                >
                                    Cancel Current Order
                                </Button>
                                <Button
                                    onClick={() => handleConfirm(confirm, validate)}
                                    disabled={!orderCancelled}
                                >
                                    Update
                                </Button>
                            </div>
                        ) : (
                            <Button onClick={() => handleConfirm(confirm, validate)}>
                                Confirm
                            </Button>
                        )
                    ) : (
                        <Button onClick={approve}>Approve</Button>
                    )
                }
            </EditPositionWrapper>
        </>
    );
};

export default EditPosition;
